// app.js — entry point.
//
// Boots the platform services, mounts the app shell, registers the service
// worker. This is the file index.html loads as a module.

import { setLanguage } from "./core/i18n.js";
import { bootstrap, services } from "./platform-services.js";
import { defineComponent } from "./core/component.js";
import { setMediaManifest } from "./data/media-paths.js";
import { registerServiceWorker } from "./sw-register.js";
import * as authStore from "./data/auth-store.js";
import { exposeServicesIfAllowed } from "./expose-services.js";

import { KFEAppShell } from "./screens/kfe-app-shell.js";
import { KFEPathway } from "./screens/kfe-pathway.js";
import { KFEPathwayList } from "./screens/kfe-pathway-list.js";
import { KFELessonDetail } from "./screens/kfe-lesson-detail.js";
import { KFEExerciseHost } from "./screens/kfe-exercise-host.js";
import { KFEL2BExercise } from "./screens/kfe-l2b-exercise.js";
import { KFEL2BTestExercise } from "./screens/kfe-l2b-test-exercise.js";
import { KFEHAAIExercise } from "./screens/kfe-haai-exercise.js";
import { KFESelfRecord } from "./screens/kfe-self-record.js";
import { KFESelfRecordGrid } from "./screens/kfe-self-record-grid.js";
import { KFERecordingControlBar } from "./ui/kfe-recording-control-bar.js";
import { KFEHtmlExercise } from "./screens/kfe-html-exercise.js";
import { KFEVideoExercise } from "./screens/kfe-video-exercise.js";
import { KFEAudioExercise } from "./screens/kfe-audio-exercise.js";
import { KFETrueFalseExercise } from "./screens/kfe-true-false-exercise.js";
import { KFEMultipleChoiceExercise } from "./screens/kfe-multiple-choice-exercise.js";
import { KFESettings } from "./screens/kfe-settings.js";
import { KFEPathwaySwitcher } from "./screens/kfe-pathway-switcher.js";
import { KFEPersonalPromise } from "./screens/kfe-personal-promise.js";
import { KFELogin } from "./screens/kfe-login.js";
import { KFEMusicTrack } from "./screens/kfe-music-track.js";
import { KFEGlobalPlaylist } from "./screens/kfe-global-playlist.js";
import { KFELessonReport } from "./screens/kfe-lesson-report.js";

import { KFETabBar } from "./ui/kfe-tab-bar.js";
import { KFENowPlaying } from "./ui/kfe-now-playing.js";
import { KFEAppMenu } from "./ui/kfe-app-menu.js";
import { KFEAudioBar } from "./ui/kfe-audio-bar.js";
import { KFEAudioPlayer } from "./ui/kfe-audio-player.js";
import { KFEItemNavBar } from "./ui/kfe-item-nav-bar.js";
import { KFEHtmlPane } from "./ui/kfe-html-pane.js";
import { KFEMusicBar } from "./ui/kfe-music-bar.js";
import { KFEPlaylistRow } from "./ui/kfe-playlist-row.js";
import { KFELyricsDisplay } from "./ui/kfe-lyrics-display.js";
import { KFETileGrid } from "./ui/kfe-tile-grid.js";
import { KFEL2BDragArea } from "./ui/kfe-l2b-drag-area.js";
import { KFEBarChart } from "./ui/kfe-bar-chart.js";
import { KFELineChart } from "./ui/kfe-line-chart.js";
import { KFEStatCard } from "./ui/kfe-stat-card.js";
import { KFEStatRow } from "./ui/kfe-stat-row.js";
import { KFESparkline } from "./ui/kfe-sparkline.js";
import { KFECircularProgress } from "./ui/kfe-circular-progress.js";
import { KFEPlayProgress } from "./ui/kfe-play-progress.js";
import { KFEProgressLine } from "./ui/kfe-progress-line.js";
import { KFESquiggle } from "./ui/kfe-squiggle.js";
import { KFECompletionScreen } from "./ui/kfe-completion-screen.js";
import { KFEDialogCompMeter } from "./ui/kfe-dialog-comp-meter.js";
import { KFEListeningMeter } from "./ui/kfe-listening-meter.js";
import { KFEBrainsoakingFamMeter } from "./ui/kfe-brainsoaking-fam-meter.js";

// Define every custom element used in the app. Order doesn't matter; tags
// resolve lazily.
defineComponent("kfe-app-shell", KFEAppShell);
defineComponent("kfe-pathway", KFEPathway);
defineComponent("kfe-pathway-list", KFEPathwayList);
defineComponent("kfe-lesson-detail", KFELessonDetail);
defineComponent("kfe-exercise-host", KFEExerciseHost);
defineComponent("kfe-l2b-exercise", KFEL2BExercise);
defineComponent("kfe-l2b-test-exercise", KFEL2BTestExercise);
defineComponent("kfe-haai-exercise", KFEHAAIExercise);
defineComponent("kfe-self-record", KFESelfRecord);
defineComponent("kfe-self-record-grid", KFESelfRecordGrid);
defineComponent("kfe-recording-control-bar", KFERecordingControlBar);
defineComponent("kfe-html-exercise", KFEHtmlExercise);
defineComponent("kfe-video-exercise", KFEVideoExercise);
defineComponent("kfe-audio-exercise", KFEAudioExercise);
defineComponent("kfe-true-false-exercise", KFETrueFalseExercise);
defineComponent("kfe-multiple-choice-exercise", KFEMultipleChoiceExercise);
defineComponent("kfe-settings", KFESettings);
defineComponent("kfe-pathway-switcher", KFEPathwaySwitcher);
defineComponent("kfe-personal-promise", KFEPersonalPromise);
defineComponent("kfe-login", KFELogin);
defineComponent("kfe-music-track", KFEMusicTrack);
defineComponent("kfe-global-playlist", KFEGlobalPlaylist);
defineComponent("kfe-lesson-report", KFELessonReport);
defineComponent("kfe-tab-bar", KFETabBar);
defineComponent("kfe-now-playing", KFENowPlaying);
defineComponent("kfe-app-menu", KFEAppMenu);
defineComponent("kfe-audio-bar", KFEAudioBar);
defineComponent("kfe-audio-player", KFEAudioPlayer);
defineComponent("kfe-item-nav-bar", KFEItemNavBar);
defineComponent("kfe-html-pane", KFEHtmlPane);
defineComponent("kfe-music-bar", KFEMusicBar);
defineComponent("kfe-playlist-row", KFEPlaylistRow);
defineComponent("kfe-lyrics-display", KFELyricsDisplay);
defineComponent("kfe-tile-grid", KFETileGrid);
defineComponent("kfe-l2b-drag-area", KFEL2BDragArea);
defineComponent("kfe-bar-chart", KFEBarChart);
defineComponent("kfe-line-chart", KFELineChart);
defineComponent("kfe-stat-card", KFEStatCard);
defineComponent("kfe-stat-row", KFEStatRow);
defineComponent("kfe-sparkline", KFESparkline);
defineComponent("kfe-circular-progress", KFECircularProgress);
defineComponent("kfe-play-progress", KFEPlayProgress);
defineComponent("kfe-progress-line", KFEProgressLine);
defineComponent("kfe-squiggle", KFESquiggle);
defineComponent("kfe-completion-screen", KFECompletionScreen);
defineComponent("kfe-dialog-comp-meter", KFEDialogCompMeter);
defineComponent("kfe-listening-meter", KFEListeningMeter);
defineComponent("kfe-brainsoaking-fam-meter", KFEBrainsoakingFamMeter);

(async () => {
  // Dev-mode reveal: visiting the app with `?dev=1` flips a localStorage
  // flag that <kfe-settings> reads to surface the Gating + Disable-dev-mode
  // rows. Per-device, never synced.
  //
  // Brief 188: dev mode also bypasses the app-shell's route guard
  // (guardPath() short-circuits on isDevMode()), but a mock session is still
  // written here — mirrors Brief 148 step 7 — so anything reading
  // isSignedIn()/getSession() (settings, a future entitlement banner) sees a
  // realistic signed-in state during development rather than relying solely
  // on the guard's separate devMode bypass. Only written if no session
  // exists yet, so a real dev login isn't clobbered on a later `?dev=1` load.
  try {
    const params = new URLSearchParams(location.search);
    if (params.get("dev") === "1") {
      if (localStorage.getItem("kfe_dev_mode") !== "true") {
        localStorage.setItem("kfe_dev_mode", "true");
        document.dispatchEvent(new Event("kfe:dev-mode-changed"));
      }
      if (!authStore.isSignedIn()) {
        authStore.setSession({
          token: "dev-bypass",
          tokenType: "Bearer",
          expiresIn: 60 * 60 * 24 * 365,
          user: { id: "dev", username: "dev", name: "Dev", licenseEndDate: "2099-12-31" },
          session: { clientType: "web" },
          device: { uaid: "dev" },
        });
      }
    }
  } catch { /* localStorage may be disabled in private mode; ignore */ }

  // Stage 1: pick a sensible default from the browser so component first
  // render isn't string-less. Don't use this if learner-store has a saved
  // preference — see stage 2.
  const navLang = (navigator.language || "en").startsWith("zh") ? "zh-Hans" : "en";
  await setLanguage(navLang);

  // Bootstrap services so we can read the saved language preference.
  await bootstrap();

  // Brief 156: expose a minimal window.services on dev / test deploys so Brief
  // 155's seed snippets reach the real learner-store funnel from the DevTools
  // Console. Hostname-gated allowlist — production NEVER exposes this; only
  // `learner` is exposed (smaller blast radius). The security invariant and the
  // rationale for hostname- (not build-flag-) gating live in expose-services.js.
  // Runs after bootstrap() (services.learner is now initialised) and before the
  // kfe:ready mount, so window.services is ready for the first console paste.
  exposeServicesIfAllowed({ services, location, win: window });

  // Stage 2: if the learner has chosen a language previously, switch to it.
  // setLanguage dispatches kfe:i18n which forces all mounted components to
  // re-render with the correct strings.
  try {
    const saved = await services.learner.getSetting("language");
    if (typeof saved === "string" && saved !== navLang) {
      await setLanguage(saved);
    }
  } catch { /* learner-store unavailable; stick with navLang */ }

  // Fire kfe:ready NOW. The app shell listens for it to register routes and
  // mount the first screen (the squiggle). Everything the squiggle needs —
  // i18n strings + the learner store — is ready above; the heavy content (the
  // unused DB is gone; MMU/item JSON is prefetching in the background) and the
  // media manifest below do NOT gate first paint (Brief 122). Dispatching here
  // rather than after the manifest fetch shaves a ~1 MB download off the
  // time-to-first-squiggle.
  document.dispatchEvent(new Event("kfe:ready"));

  // If a precomputed media manifest is available, use it. Without it
  // media-paths.js falls back to per-request HEAD probes which are slow
  // and brittle in production. Build with `npm run build:media-manifest`.
  //
  // Loaded AFTER kfe:ready (Brief 122): the manifest (~1 MB) is only needed
  // for media playback inside exercises, never for the squiggle. Fetching it
  // off the critical path keeps first paint fast; until it lands, media-paths
  // falls back to HEAD probes — but only for a learner who reaches an exercise
  // in the sub-second window before it arrives, and exercise entry itself
  // awaits the larger MMU/item JSON, so in practice the manifest is always in
  // before any media lookup.
  fetch("/content/media-manifest.json")
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => { if (m) setMediaManifest(m); })
    .catch(() => { /* no manifest, fall back to HEAD probes */ });

  // Register the service worker last — the app must work without it. Registers
  // in prod, unregisters leftover SWs in dev (a cache-first SW would otherwise
  // mask local JS edits). See sw-register.js. Fire-and-forget: the app is fully
  // usable whether or not the SW comes up.
  registerServiceWorker();
})();
